import { useEffect, useRef, useState } from "react"
import { GetApp } from "../../../App"
import { Session } from "../../../App/Session"
import { logger } from "../../../App/Logger"
import { AppViewData } from "../Context"

interface SelectedShipLoaderOptions<T> {
  shipId?: number;
  loadedShipId?: number;
  load: (shipId: number) => Promise<T | undefined>;
  onLoaded: (shipId: number, value: T) => void;
  onMissing: () => void;
}

export function useSelectedShipLoader<T>({
  shipId,
  loadedShipId,
  load,
  onLoaded,
  onMissing,
}: SelectedShipLoaderOptions<T>): void {
  const app = GetApp();
  const [retry, setRetry] = useState({ shipId: undefined as number | undefined, attempt: 0 });
  const syncWaits = useRef({ shipId: undefined as number | undefined, count: 0 });
  const attempt = retry.shipId == shipId ? retry.attempt : 0;

  useEffect(() => {
    let cancelled = false;
    let retryTimer: number | undefined;

    const run = async () => {
      if (shipId == undefined || loadedShipId == shipId) {
        return;
      }
      logger.debug("loading ship", shipId, attempt);
      try {
        const value = await load(shipId);
        if (cancelled) {
          return;
        }
        if (value == undefined) {
          logger.error("Ship load returned no ship", shipId);
          onMissing();
          return;
        }
        onLoaded(shipId, value);
        syncWaits.current = { shipId, count: 0 };
        if (attempt != 0) {
          setRetry({ shipId, attempt: 0 });
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        const maxAttempts = 5;
        if (Session.classifyShipLoadFailure(error) == "retry") {
          const syncStatus = await AppViewData.syncStatus(app);
          if (cancelled) {
            return;
          }
          const waitsForSync = Session.shipLoadWaitsForSync(syncStatus);
          if (syncWaits.current.shipId != shipId) {
            syncWaits.current = { shipId, count: 0 };
          }
          const budgeted = attempt - syncWaits.current.count;
          if (waitsForSync) {
            syncWaits.current.count += 1;
          }
          if (waitsForSync || budgeted + 1 < maxAttempts) {
            const delay = Session.shipLoadRetryDelay(waitsForSync ? undefined : budgeted);
            logger.debug("ship load failed transiently, retrying", shipId, attempt, waitsForSync, error);
            retryTimer = window.setTimeout(() => {
              if (!cancelled) {
                setRetry({ shipId, attempt: attempt + 1 });
              }
            }, delay);
            return;
          }
        }
        logger.error("Error loading ship:", error);
        onMissing();
      }
    };

    void run();
    return () => {
      cancelled = true;
      if (retryTimer != undefined) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [app, attempt, load, loadedShipId, onLoaded, onMissing, shipId]);
}
